import React from "react";
import { Head, router, usePage } from "@inertiajs/react";
import styled from "styled-components";
import { GlobalStyle } from "../../constants/global-style";
import ProvideLoggedInUser from "../../providers/ProvideLoggedInUser";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import ExamApp from "../../exams/App";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../../constants/styles";

// Inertia entry for `/exams/:id` when `exam_rebrand_v1` is ON. The
// legacy AJAX endpoints (`/ajax_answer_exam`, `/ajax_submit_exam`)
// are reused unchanged — only the surface is new.
export default function Take() {
  const {
    theme = "light",
    package: pkg = {},
    user = {},
    attempt = {},
    questions = [],
    endpoints = {},
    keyboard_hints: keyboardHints = [],
  } = usePage().props;

  const [activeIdx, setActiveIdx] = React.useState(0);
  const [answers, setAnswers] = React.useState(() => {
    const init = {};
    questions.forEach((q) => {
      if (q.saved_answer_id) init[q.id] = q.saved_answer_id;
    });
    return init;
  });
  const [savingIds, setSavingIds] = React.useState({});
  const [submitConfirm, setSubmitConfirm] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const activeQuestion = questions[activeIdx];

  // Server-authoritative timer — derive remaining once per second from
  // start_exam_at + duration_seconds. Background-tab safe.
  const [remaining, setRemaining] = React.useState(() => attempt.duration_seconds || 0);
  // `submitRef` lets the timer trigger an auto-submit without forming a
  // circular dep on handleSubmit (which is declared below).
  const submitRef = React.useRef(null);
  React.useEffect(() => {
    const startMs = attempt.start_exam_at ? new Date(attempt.start_exam_at).getTime() : Date.now();
    const totalSec = Number(attempt.duration_seconds) || 0;
    if (!totalSec) return undefined;

    const tick = () => {
      const elapsed = (Date.now() - startMs) / 1000;
      const next = Math.max(0, Math.round(totalSec - elapsed));
      setRemaining(next);
      if (next <= 0) {
        clearInterval(id);
        if (typeof submitRef.current === "function") submitRef.current();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [attempt.start_exam_at, attempt.duration_seconds]);

  const handleAnswer = React.useCallback(
    async (questionId, answerId) => {
      setAnswers((prev) => ({ ...prev, [questionId]: answerId }));
      setSavingIds((prev) => ({ ...prev, [questionId]: "saving" }));
      try {
        const fd = new FormData();
        fd.append("question_id", questionId);
        fd.append("answer_id", answerId);
        // Legacy endpoint expects form-encoded params (Stimulus
        // controller sent it that way too).
        await fetch(endpoints.answer_url, {
          method: "POST",
          body: fd,
          headers: { "X-CSRF-Token": getCsrfFromMeta() },
          credentials: "same-origin",
        });
        setSavingIds((prev) => ({ ...prev, [questionId]: "saved" }));
        // Clear saved indicator after 1.5s
        setTimeout(() => {
          setSavingIds((prev) => {
            const next = { ...prev };
            if (next[questionId] === "saved") delete next[questionId];
            return next;
          });
        }, 1500);
      } catch (e) {
        console.warn("[exam] answer save failed", e);
        setSavingIds((prev) => ({ ...prev, [questionId]: "error" }));
      }
    },
    [endpoints.answer_url]
  );

  const handleSubmit = React.useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("score", "5");
      fd.append("comment", "");
      const resp = await fetch(endpoints.submit_url, {
        method: "POST",
        body: fd,
        headers: { "X-CSRF-Token": getCsrfFromMeta() },
        credentials: "same-origin",
      });
      if (resp.ok) {
        if (endpoints.review_url) router.visit(endpoints.review_url);
      } else {
        setSubmitting(false);
      }
    } catch (e) {
      console.warn("[exam] submit failed", e);
      setSubmitting(false);
    }
  }, [endpoints.submit_url, endpoints.review_url, submitting]);

  // Keep the timer's auto-submit reference in sync with the latest
  // handleSubmit closure.
  React.useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Keyboard shortcuts — 1-4 select option, ←/→ navigate, Enter
  // saves-and-advances. Bail when a form element has focus.
  React.useEffect(() => {
    const handler = (e) => {
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (["input", "textarea", "select", "button"].includes(tag) || document.activeElement?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!activeQuestion) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (activeIdx < totalQuestions - 1) setActiveIdx(activeIdx + 1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (activeIdx > 0) setActiveIdx(activeIdx - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIdx < totalQuestions - 1) setActiveIdx(activeIdx + 1);
      } else if (e.key === "Escape") {
        setSubmitConfirm(false);
      } else {
        const num = parseInt(e.key, 10);
        if (!Number.isNaN(num) && num >= 1 && num <= activeQuestion.options.length) {
          e.preventDefault();
          const opt = activeQuestion.options[num - 1];
          if (opt) handleAnswer(activeQuestion.id, opt.id);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeIdx, activeQuestion, totalQuestions, handleAnswer]);

  if (!activeQuestion) {
    return (
      <ProvideLoggedInUser>
        <ExamApp theme={theme}>
          <Inner>
            <EmptyState>Tidak ada soal pada ujian ini.</EmptyState>
          </Inner>
        </ExamApp>
      </ProvideLoggedInUser>
    );
  }

  const lowTime = remaining <= 300; // last 5 minutes

  return (
    <>
      <Head>
        <title>{`Ujian ${pkg.curriculum_name || ""} — Taxsam Learning Center`}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <GlobalStyle />
      <ProvideLoggedInUser>
        <ExamApp theme={theme}>
          <Inner>
            <Layout>
              <SectionErrorBoundary name="ExamCanvas">
                <Canvas>
                  <QuestionHead>
                    <QNumber>No. {activeIdx + 1}</QNumber>
                    <SaveStatus state={savingIds[activeQuestion.id]}>
                      {savingIds[activeQuestion.id] === "saving" && "Menyimpan…"}
                      {savingIds[activeQuestion.id] === "saved" && "Tersimpan ✓"}
                      {savingIds[activeQuestion.id] === "error" && "Gagal — coba lagi"}
                    </SaveStatus>
                  </QuestionHead>
                  <QBody dangerouslySetInnerHTML={{ __html: activeQuestion.body_html }} />
                  <Options>
                    {activeQuestion.options.map((opt, i) => {
                      const letter = String.fromCharCode(65 + i);
                      const selected = answers[activeQuestion.id] === opt.id;
                      return (
                        <OptionCard
                          key={opt.id}
                          $selected={selected}
                          onClick={() => handleAnswer(activeQuestion.id, opt.id)}
                          type="button"
                        >
                          <OptionLetter $selected={selected}>{letter}</OptionLetter>
                          <OptionBody dangerouslySetInnerHTML={{ __html: opt.body_html }} />
                        </OptionCard>
                      );
                    })}
                  </Options>
                </Canvas>
              </SectionErrorBoundary>

              <SectionErrorBoundary name="ExamRail">
                <Rail>
                  <RailHead>
                    <RailExamName>Ujian {pkg.curriculum_name}</RailExamName>
                    <RailUserName>{user.name}</RailUserName>
                  </RailHead>
                  <Timer $low={lowTime}>
                    <TimerIcon aria-hidden="true">⏱</TimerIcon>
                    <TimerValue>{formatHMS(remaining)}</TimerValue>
                  </Timer>
                  <RailCta type="button" onClick={() => setActiveIdx(Math.min(totalQuestions - 1, activeIdx + 1))}>
                    Soal Selanjutnya →
                  </RailCta>
                  <Grid>
                    {questions.map((q, idx) => (
                      <GridCell
                        key={q.id}
                        $active={idx === activeIdx}
                        $answered={!!answers[q.id]}
                        onClick={() => setActiveIdx(idx)}
                        type="button"
                      >
                        {idx + 1}
                      </GridCell>
                    ))}
                  </Grid>
                  <RailCounters>
                    <strong>{answeredCount}</strong> dari <strong>{totalQuestions}</strong> dijawab
                  </RailCounters>
                  <SubmitCta type="button" onClick={() => setSubmitConfirm(true)} disabled={submitting}>
                    {submitting ? "Mengumpulkan…" : "Simpan Soal"}
                  </SubmitCta>
                  {keyboardHints.length > 0 && (
                    <KbdHelp>
                      {keyboardHints.map((h) => (
                        <KbdRow key={h.key}>
                          <Kbd>{h.key}</Kbd>
                          <span>{h.label}</span>
                        </KbdRow>
                      ))}
                    </KbdHelp>
                  )}
                </Rail>
              </SectionErrorBoundary>
            </Layout>

            {submitConfirm && (
              <Backdrop onClick={() => setSubmitConfirm(false)}>
                <Modal onClick={(e) => e.stopPropagation()}>
                  <ModalTitle>Kumpulkan Jawaban?</ModalTitle>
                  <ModalBody>
                    Anda telah menjawab <strong>{answeredCount}</strong> dari{" "}
                    <strong>{totalQuestions}</strong> soal.
                    {answeredCount < totalQuestions && (
                      <span> Soal yang belum dijawab tidak akan mendapat skor.</span>
                    )}
                  </ModalBody>
                  <ModalActions>
                    <ModalSecondary type="button" onClick={() => setSubmitConfirm(false)}>
                      Periksa Lagi
                    </ModalSecondary>
                    <ModalPrimary type="button" disabled={submitting} onClick={handleSubmit}>
                      {submitting ? "Mengumpulkan…" : "Kumpulkan Jawaban"}
                    </ModalPrimary>
                  </ModalActions>
                </Modal>
              </Backdrop>
            )}
          </Inner>
        </ExamApp>
      </ProvideLoggedInUser>
    </>
  );
}

function getCsrfFromMeta() {
  if (typeof document === "undefined") return "";
  const el = document.querySelector('meta[name="csrf-token"]');
  return el?.getAttribute("content") || "";
}

function formatHMS(secs) {
  const total = Math.max(0, Number(secs) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  return `${String(m).padStart(2, "0")}.${String(s).padStart(2, "0")}`;
}

// ---- Styles ----------------------------------------------------------------

const Inner = styled.div`
  ${outerContainerCss}
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 32px;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const Canvas = styled.div`
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  padding: 26px 30px;
  box-shadow: ${card_shadow_md};

  @media ${narrowScreenMediaQuery} { padding: 18px 18px; }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: none;
  }
`;

const QuestionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 12px;
`;

const QNumber = styled.span`
  display: inline-flex;
  padding: 6px 14px;
  border-radius: 8px;
  background: #1f2937;
  color: #ffffff;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.06em;
`;

const SaveStatus = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${({ state }) =>
    state === "saved"   ? "#047857" :
    state === "error"   ? "#b91c1c" :
                          "#6b7280"};
`;

const QBody = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
  margin-bottom: 18px;

  [data-theme="dark"] & { color: #e5e7eb; }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionCard = styled.button`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 12px;
  background: ${({ $selected }) =>
    $selected ? "rgba(216, 89, 7, 0.08)" : "rgba(15, 23, 42, 0.02)"};
  border: 1px solid ${({ $selected }) =>
    $selected ? "rgba(216, 89, 7, 0.45)" : "rgba(15, 23, 42, 0.10)"};
  cursor: pointer;
  text-align: left;
  transition: background 150ms ease, border-color 150ms ease, transform 100ms ease;
  font: inherit;
  color: inherit;

  &:hover {
    transform: translateY(-1px);
    border-color: ${brand_orange_deep};
  }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }

  [data-theme="dark"] & {
    background: ${({ $selected }) => ($selected ? "rgba(216, 89, 7, 0.15)" : "rgba(255, 255, 255, 0.03)")};
    border-color: ${({ $selected }) => ($selected ? "rgba(216, 89, 7, 0.5)" : "rgba(255, 255, 255, 0.10)")};
  }
`;

const OptionLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $selected }) => ($selected ? brand_gradient : "rgba(15, 23, 42, 0.06)")};
  color: ${({ $selected }) => ($selected ? "#ffffff" : "#4b5563")};
  font-weight: 800;
  font-size: 13px;

  [data-theme="dark"] & {
    color: ${({ $selected }) => ($selected ? "#fff" : "#cbd5e1")};
    background: ${({ $selected }) => ($selected ? brand_gradient : "rgba(255, 255, 255, 0.08)")};
  }
`;

const OptionBody = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;

  [data-theme="dark"] & { color: #e5e7eb; }
`;

const Rail = styled.aside`
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 16px;
  box-shadow: ${card_shadow_md};

  @media (max-width: 1023px) { position: static; }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: none;
  }
`;

const RailHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RailExamName = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: #1f2937;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const RailUserName = styled.span`
  font-size: 12px;
  color: #6b7280;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const Timer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  background: ${({ $low }) => ($low ? "rgba(220, 38, 38, 0.10)" : brand_gradient)};
  color: ${({ $low }) => ($low ? "#b91c1c" : "#ffffff")};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  ${({ $low }) => $low && "animation: tlc-low 1.6s ease-in-out infinite;"}

  @keyframes tlc-low {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const TimerIcon = styled.span`
  font-size: 16px;
`;

const TimerValue = styled.span`
  font-size: 22px;
  letter-spacing: 0.04em;
`;

const RailCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: none;
  cursor: pointer;

  &:hover { filter: brightness(0.97); }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
`;

const GridCell = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 8px;
  border: ${({ $active }) =>
    $active ? `2px solid ${brand_orange_deep}` : "1px solid rgba(15,23,42,0.08)"};
  background: ${({ $answered, $active }) =>
    $active     ? "rgba(216, 89, 7, 0.08)" :
    $answered   ? brand_gradient :
                  "rgba(15, 23, 42, 0.02)"};
  color: ${({ $answered, $active }) =>
    $active     ? brand_orange_deep :
    $answered   ? "#ffffff" :
                  "#4b5563"};
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  transition: filter 150ms ease;

  &:hover { filter: brightness(0.97); }

  [data-theme="dark"] & {
    background: ${({ $answered, $active }) =>
      $active     ? "rgba(216, 89, 7, 0.18)" :
      $answered   ? brand_gradient :
                    "rgba(255, 255, 255, 0.05)"};
    color: ${({ $answered, $active }) =>
      $active     ? "#fdba74" :
      $answered   ? "#fff" :
                    "#cbd5e1"};
  }
`;

const RailCounters = styled.span`
  font-size: 12px;
  color: #4b5563;

  strong { color: ${brand_orange_deep}; }

  [data-theme="dark"] & { color: #cbd5e1; }
`;

const SubmitCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: none;
  cursor: pointer;

  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;

const KbdHelp = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 10px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);

  [data-theme="dark"] & { border-top-color: rgba(255, 255, 255, 0.08); }
`;

const KbdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #6b7280;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const Kbd = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.12);
  font-family: "JetBrains Mono", "SFMono-Regular", Menlo, monospace;
  font-size: 10px;
  font-weight: 800;
  color: #1f2937;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    color: #f3f4f6;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  background: #ffffff;
  border-radius: 18px;
  padding: 26px 28px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.3);

  [data-theme="dark"] & {
    background: #17171e;
    color: #f3f4f6;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 800;
  color: ${brand_orange_deep};
`;

const ModalBody = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0 0 18px;

  strong { color: ${brand_orange_deep}; }

  [data-theme="dark"] & { color: #cbd5e1; }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ModalSecondary = styled.button`
  padding: 10px 18px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: transparent;
  color: #4b5563;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover { background: rgba(15, 23, 42, 0.04); }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }

  [data-theme="dark"] & { color: #cbd5e1; border-color: rgba(255,255,255,0.12); }
`;

const ModalPrimary = styled.button`
  padding: 10px 22px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: none;
  cursor: pointer;

  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;

const EmptyState = styled.div`
  background: rgba(15, 23, 42, 0.02);
  border: 1px dashed rgba(15, 23, 42, 0.12);
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;
